import { INLINE_EDIT_FIELD_TEST_ID } from '@e2e/constants/exam-management';
import {
  Box,
  EditOutlinedIcon,
  EllipsisText,
  Flex,
  InputBase,
  inputBaseClasses,
  mergeSx,
  styled,
  type InputBaseProps,
  type BoxProps,
  type EllipsisTextProps,
  type FlexProps,
  type SvgIconProps,
  type TextProps,
} from '@shared/components';
import { useClickOutside } from '@shared/hooks';
import { useLogger } from '@shared/logger';
import { useEffect, useRef, useState } from 'react';
import { useController, type FieldValues, type UseControllerProps } from 'react-hook-form';

import ActionButtonGroup from './action-button-group';

const StyledInputBase = styled(InputBase)(({ theme, error }) => ({
  borderRadius: theme.spacing(1),
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  gap: theme.spacing(2),
  alignItems: 'flex-start',
  border: '1px solid',
  backgroundColor: theme.palette.common.white,
  borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
  ' .MuiInputBase-input': {
    ...theme.typography.body2,
    padding: theme.spacing(0),
  },
  '&:hover': {
    borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
  },
}));

const TextWrapper = styled(Flex)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  padding: theme.spacing(1, 2),
  gap: theme.spacing(2),
  border: '1px solid transparent',
  '&:hover': {
    '> svg': {
      color: theme.palette.primary.main,
    },
  },
}));

enum Mode {
  DISPLAY = 'DISPLAY',
  EDITING = 'EDITING',
}

export type InlineEditFieldProps<TFieldValues extends FieldValues = FieldValues> = InputBaseProps &
  UseControllerProps<TFieldValues> & {
    helperText?: string;
    maxLines?: number;
    onChangeMode?: (mode: Mode) => Promise<void>;
    onConfirm?: (prevValue?: string | null) => void;
    onDiscard?: () => void;
    onClickOutside?: () => void;
    allowEmpty?: boolean;
    nonInfo?: boolean;
    variant?: TextProps['variant'];
    slotProps?: {
      fieldWrapper?: BoxProps;
      textWrapper?: FlexProps;
      text?: Omit<EllipsisTextProps, 'slotProps' | 'children'>;
      editIcon?: SvgIconProps;
      helperText?: EllipsisTextProps;
    } & EllipsisTextProps['slotProps'];
  };

export default function InlineEditField<TFieldValues extends FieldValues = FieldValues>(
  props: Readonly<InlineEditFieldProps<TFieldValues>>,
) {
  const {
    name,
    control,
    rules,
    defaultValue,
    shouldUnregister,
    disabled: isDisabled,
    placeholder,
    helperText,
    maxLines = 1,
    variant = 'body2',
    autoComplete = 'off',
    slotProps = {},
    onChangeMode,
    onConfirm,
    onDiscard,
    onClickOutside,
    allowEmpty: shouldAllowEmpty = false,
    nonInfo: isNonInfo = false,
    ...restProps
  } = props;
  const {
    fieldWrapper: fieldWrapperProps,
    textWrapper: textWrapperProps,
    text: textProps,
    editIcon: editIconProps,
    helperText: helperTextProps,
    ...ellipsisTextSlotProps
  } = slotProps;

  const logger = useLogger('InlineEditField');
  const [mode, setMode] = useState(Mode.DISPLAY);
  const [oldValue, setOldValue] = useState(defaultValue);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const clickOutsideRef = useClickOutside(() => {
    if (mode === Mode.EDITING) {
      onClickOutside?.();
    }
  });

  const { field, formState } = useController({
    name,
    control,
    rules,
    shouldUnregister,
    defaultValue,
  });

  const error = formState.errors[field.name];
  const errorMessages = error?.message;
  const isError = Boolean(error);

  const helperTextOnEdit = (errorMessages as string) ?? helperText;
  const isFieldEmpty = !field.value;
  const isDisplaying = mode === Mode.DISPLAY;

  const focusEndOfInput = () => {
    setTimeout(() => {
      inputRef.current?.focus();
      if (field.value) {
        const length = field.value.length;
        inputRef.current?.setSelectionRange(length, length);
      }
    }, 0);
  };

  const handleChangeMode = (newMode: Mode) => {
    onChangeMode?.(newMode)
      .then(() => {
        if (newMode === Mode.DISPLAY) {
          setMode(Mode.DISPLAY);
        } else {
          setMode(Mode.EDITING);
          focusEndOfInput();
        }
      })
      .catch((error) => {
        logger.debug(error);
      });
  };

  const changeToDisplayMode = () => {
    handleChangeMode(Mode.DISPLAY);
  };

  const changeToEditMode = () => {
    handleChangeMode(Mode.EDITING);
  };

  const handleClickEdit = () => {
    if (isDisabled) {
      return;
    }
    setOldValue(field.value);
    changeToEditMode();
  };

  const handleDiscard = () => {
    onDiscard?.();
    if (field.value === null || isNonInfo) {
      field.onChange(null);
      changeToDisplayMode();
      return;
    }
    field.onChange(oldValue);
    changeToDisplayMode();
  };

  const handleConfirm = () => {
    if (error && field?.value?.length > 0) {
      if (error.type === 'onBlur') {
        if (isNonInfo) {
          field.onChange(null);
          changeToDisplayMode();
          return;
        }
        if (formState.errors[field.name]) {
          field.onChange(field.value);
          focusEndOfInput();
          changeToDisplayMode();
          return;
        }
      } else if (error.type !== 'onBlur') {
        focusEndOfInput();
        return;
      }
    }

    onConfirm?.(oldValue);
    changeToDisplayMode();

    const isInvalidEmpty = !shouldAllowEmpty && isFieldEmpty && field.value !== '' && !isNonInfo;
    if (isInvalidEmpty) {
      field.onChange(oldValue);
    } else {
      field.onChange(field.value === '' ? null : field.value);
      setOldValue(field.value);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleConfirm();
    }
    if (event.key === 'Escape') {
      handleDiscard();
    }
  };

  const handleTextWrapperKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      changeToEditMode();
    }
  };

  useEffect(() => {
    if (error && mode === Mode.DISPLAY) {
      setMode(Mode.EDITING);
    }
  }, [error, mode]);

  return (
    <Box
      data-testid={INLINE_EDIT_FIELD_TEST_ID.INPUT}
      onClick={(event) => {
        event.stopPropagation();
      }}
      {...fieldWrapperProps}
      ref={clickOutsideRef}
      sx={mergeSx(
        {
          width: isDisplaying ? 'fit-content' : '100%',
        },
        fieldWrapperProps?.sx,
      )}
    >
      {isDisplaying ? (
        <TextWrapper
          tabIndex={0}
          onClick={handleClickEdit}
          onKeyDown={handleTextWrapperKeyDown}
          {...textWrapperProps}
          sx={mergeSx(
            (theme) => ({
              cursor: isDisabled ? 'default' : 'pointer',
              gap: theme.spacing(2),
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                bgcolor: isDisabled ? undefined : theme.palette.grey[100],
              },
            }),
            textWrapperProps?.sx,
          )}
        >
          <EllipsisText
            {...textProps}
            maxLines={maxLines}
            variant={variant}
            slotProps={ellipsisTextSlotProps}
            sx={mergeSx(
              (theme) => ({
                color: theme.palette.text.primary,
              }),
              textProps?.sx,
            )}
          >
            {isFieldEmpty ? placeholder : field.value}
          </EllipsisText>
          {isDisabled ? null : (
            <EditOutlinedIcon
              {...editIconProps}
              sx={mergeSx(
                {
                  fontSize: '20px',
                },
                editIconProps?.sx,
              )}
            />
          )}
        </TextWrapper>
      ) : (
        <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(0.5) }}>
          <StyledInputBase
            {...field}
            {...restProps}
            fullWidth
            error={isError}
            placeholder={placeholder}
            multiline={maxLines > 1}
            maxRows={maxLines}
            inputRef={inputRef}
            autoComplete={autoComplete}
            onKeyDown={handleKeyDown}
            endAdornment={<ActionButtonGroup onConfirm={handleConfirm} onDiscard={handleDiscard} />}
            slotProps={{
              input: {
                sx: (theme) => {
                  const textStyle = theme.typography[
                    variant as keyof typeof theme.typography
                  ] as React.CSSProperties;
                  return {
                    [`&&.${inputBaseClasses.input}`]: {
                      ...textStyle,
                      height: 'unset',
                      minWidth: '18px',
                    },
                  };
                },
              },
            }}
            sx={{
              [`&.${inputBaseClasses.root}`]: {
                alignItems: 'center',
                flex: 1,
              },
            }}
          />
          {helperTextOnEdit ? (
            <EllipsisText
              variant='caption'
              color={isError ? 'error' : 'text.secondary'}
              {...helperTextProps}
            >
              {helperTextOnEdit}
            </EllipsisText>
          ) : null}
        </Flex>
      )}
    </Box>
  );
}
